import { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import type { StageHistoryEntry } from "@/hooks/use-pipeline";
import { resolveStageDisplay } from "@/lib/pipeline-display";
import type { PipelineRow } from "@/types/pipeline";
import { StickyNote, ChevronRight, Clock } from "lucide-react";

interface StageNotesDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  pipelines: PipelineRow[];
  propertyAddress: string;
  /** Either a pipeline_id, pipeline title, or legacy snake_case stage name. */
  currentStage: string;
  currentNotes: string | null;
  stageHistory: StageHistoryEntry[];
  onSaveCurrentNote: (notes: string) => Promise<void>;
  onSaveStageNote: (historyId: string, notes: string) => Promise<void>;
}

function formatDate(d: string) {
  return new Date(d).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

export function StageNotesDialog({
  open,
  onOpenChange,
  pipelines,
  propertyAddress,
  currentStage,
  currentNotes,
  stageHistory,
  onSaveCurrentNote,
  onSaveStageNote,
}: StageNotesDialogProps) {
  const [currentNoteText, setCurrentNoteText] = useState(currentNotes || "");
  const [editingHistoryId, setEditingHistoryId] = useState<string | null>(null);
  const [editingText, setEditingText] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setCurrentNoteText(currentNotes || "");
  }, [currentNotes, open]);

  const handleSaveCurrent = async () => {
    setSaving(true);
    await onSaveCurrentNote(currentNoteText);
    setSaving(false);
  };

  const handleSaveHistory = async (historyId: string) => {
    setSaving(true);
    await onSaveStageNote(historyId, editingText);
    setEditingHistoryId(null);
    setSaving(false);
  };

  const currentDisplay = resolveStageDisplay(currentStage, pipelines);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-base">
            <StickyNote className="h-4 w-4 text-primary" />
            Notes — {propertyAddress}
          </DialogTitle>
        </DialogHeader>

        {/* Current stage note */}
        <div className="space-y-2">
          <div className="flex items-center gap-2">
            <span
              className="w-2 h-2 rounded-full"
              style={{ backgroundColor: currentDisplay.color }}
            />
            <span className="text-sm font-medium text-foreground">
              {currentDisplay.label}
            </span>
            <Badge variant="outline" className="text-[10px] h-4">
              Current
            </Badge>
          </div>
          <Textarea
            value={currentNoteText}
            onChange={(e) => setCurrentNoteText(e.target.value)}
            placeholder={`Add notes for ${currentDisplay.label} stage...`}
            className="min-h-[80px] text-sm"
          />
          <Button
            size="sm"
            onClick={handleSaveCurrent}
            disabled={saving || currentNoteText === (currentNotes || "")}
            className="h-7 text-xs"
          >
            Save Note
          </Button>
        </div>

        {/* Stage history notes */}
        {stageHistory.length > 0 && (
          <div className="space-y-3 pt-2 border-t border-border">
            <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
              Stage History
            </p>
            {[...stageHistory].reverse().map((entry) => {
              const fromDisplay = entry.from_pipeline_id
                ? resolveStageDisplay(entry.from_pipeline_id, pipelines)
                : null;
              const toDisplay = resolveStageDisplay(
                entry.to_pipeline_id,
                pipelines,
              );
              return (
                <div
                  key={entry.id}
                  className="space-y-1.5 rounded-md border border-border/50 bg-muted/20 p-3"
                >
                  <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                    {fromDisplay && (
                      <>
                        <span
                          className="w-1.5 h-1.5 rounded-full"
                          style={{ backgroundColor: fromDisplay.color }}
                        />
                        <span>{fromDisplay.label}</span>
                        <ChevronRight className="h-3 w-3" />
                      </>
                    )}
                    <span
                      className="w-1.5 h-1.5 rounded-full"
                      style={{ backgroundColor: toDisplay.color }}
                    />
                    <span className="font-medium text-foreground">
                      {toDisplay.label}
                    </span>
                    <span className="ml-auto flex items-center gap-1">
                      <Clock className="h-3 w-3" />
                      {formatDate(entry.changed_at)}
                    </span>
                  </div>

                  {editingHistoryId === entry.id ? (
                    <div className="space-y-1.5">
                      <Textarea
                        value={editingText}
                        onChange={(e) => setEditingText(e.target.value)}
                        placeholder="Add a note for this transition..."
                        className="min-h-[60px] text-xs"
                      />
                      <div className="flex gap-1.5">
                        <Button
                          size="sm"
                          className="h-6 text-[11px]"
                          onClick={() => handleSaveHistory(entry.id)}
                          disabled={saving}
                        >
                          Save
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          className="h-6 text-[11px]"
                          onClick={() => setEditingHistoryId(null)}
                        >
                          Cancel
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <div
                      className="text-xs text-muted-foreground cursor-pointer hover:text-foreground transition-colors"
                      onClick={() => {
                        setEditingHistoryId(entry.id);
                        setEditingText(entry.notes || "");
                      }}
                    >
                      {entry.notes ? (
                        <p className="text-foreground">{entry.notes}</p>
                      ) : (
                        <p className="italic text-muted-foreground/60">
                          Click to add note...
                        </p>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" size="sm" onClick={() => onOpenChange(false)}>
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
